import React, { useCallback, useEffect, useState } from 'react'
import { CAlert, CBadge, CButton, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload, cilWarning } from '@coreui/icons'
import moment from 'moment'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import { InfoBox } from 'src/components/common/ImportWizard'

// The tradeLog endpoint only filters on full_name.
const SEARCH_FIELDS = [{ key: 'full_name', label: 'Member' }]

const columns = [
  { key: 'trade_created_at', label: 'Date', filter: false, _style: { width: '25%' } },
  { key: 'full_name', label: 'Member', filter: false },
  { key: 'totalTrades', label: 'Trades Made', filter: false, _style: { width: '18%' } },
]

const TradeLog = ({ onTotalChange }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [users, setUsers] = useState([])

  const fetchUsers = useCallback(() => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    const term = searchValue.trim()
    let params = new URLSearchParams()
    term && params.append(searchField, term)
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    PlayerService.tradeLog(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setUsers(result.data)
        if (!term) onTotalChange?.(result.data?.total ?? 0)
      })
      .catch(() => {
        setError(true)
        ToastComponent('Could not load the trade log', 'error')
      })
      .finally(() => setLoading(false))
  }, [activePage, searchField, searchValue, columnSorter, itemsPerPage, onTotalChange])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const noItemsLabel = searchValue.trim()
    ? 'No trades match your search'
    : 'No trades have been made yet'

  return (
    <>
      <InfoBox>
        One row per trade entry a member has made, newest first. Trades Made is how many trades that
        entry used.
      </InfoBox>

      {error ? (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2 mb-0">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load the trade log.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchUsers}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      ) : (
        <>
          <TableSearchToolbar
            fields={SEARCH_FIELDS}
            field={searchField}
            onFieldChange={(field) => {
              setSearchField(field)
              setSearchValue('')
              setActivePage(1)
            }}
            value={searchValue}
            onSearch={(value) => {
              setActivePage(1)
              setSearchValue(value)
            }}
            placeholder="Search members..."
          />

          <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              scopedColumns={{
                trade_created_at: (item) => (
                  <td>
                    {item.trade_created_at ? (
                      <>
                        <div className="tr-date">
                          {moment(item.trade_created_at).format('D MMM YYYY')}
                        </div>
                        <div className="tr-member-meta">
                          {moment(item.trade_created_at).format('h:mm A')}
                        </div>
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                ),
                full_name: (item) => (
                  <td>
                    <div className="tr-member-name">{item.full_name || 'Unnamed member'}</div>
                    {item.email && <div className="tr-member-meta">{item.email}</div>}
                  </td>
                ),
                totalTrades: (item) => {
                  const count = Number(item.totalTrades) || 0
                  return (
                    <td>
                      <CBadge shape="rounded-pill" color="primary">
                        {count} trade{count !== 1 ? 's' : ''}
                      </CBadge>
                    </td>
                  )
                },
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              noItemsLabel={noItemsLabel}
              pagination={{ external: true }}
              paginationProps={{
                activePage: activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(activePage) => setActivePage(activePage)}
              onItemsPerPageChange={(itemsPerPage) => {
                setActivePage(1)
                setItemsPerPage(itemsPerPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </>
      )}
    </>
  )
}

export default TradeLog
